import { useState, useEffect } from "react";
import { Form, Row, Col, Input, Typography, Button, Switch, Tooltip, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from 'types';
import { useRegisterForm } from "./useRegisterForm";
import { useNavigate } from 'react-router-dom';
import Turnstile from 'react-turnstile';
import axios from "axios";
import moment from "moment";
import { trackAnalyticsEvent } from 'analytics/ga4';

const cloudflareSiteKey = process.env.REACT_APP_CLOUDFLARE_SITE_KEY || '';

export default function RegisterForm () {

  const {
    register,
    isRegistering,
    hasRegistered,
    failedRegistering,
  } = useRegisterForm();

  const [form] = Form.useForm();
  const { t }: TFunction = useTranslation();
  const navigate = useNavigate();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [showTermsAlert, setShowTermsAlert] = useState(false);

  const logFirstLogin = async (accountEmail: string) => {
    try {
      const date = encodeURIComponent(moment().format('YYYY-MM-DD HH:mm:ss'));
      const email = encodeURIComponent(accountEmail);
      const query = `?date=${date}&email=${email}`;
      await Promise.all([
        axios.get(`https://hooks.zapier.com/hooks/catch/14247483/2avwkth/${query}`),
        axios.get(`https://hooks.zapier.com/hooks/catch/14247483/495vph4/${query}`),
      ]);
    } catch (error) {
      console.error('Error sending data to Zapier:', error);
    }
  };

  useEffect(() => {
    trackAnalyticsEvent('reg_flow_register_view');
  }, []);

  useEffect(() => {
    if (hasRegistered) {
      trackAnalyticsEvent('reg_flow_register_success');
      logFirstLogin(form.getFieldValue('email'));
      navigate('/validate', { state: { email: form.getFieldValue('email'), password: form.getFieldValue('password') } });
    }
  }, [form, hasRegistered, navigate]);

  useEffect(() => {
    if (failedRegistering) {
      trackAnalyticsEvent('reg_flow_register_error');
    }
  }, [failedRegistering]);

  const SESSION_KEY_EMAIL = 'lsp_reg_email';
  const SESSION_KEY_PASSWORD = 'lsp_reg_password';

  const onFinish = (values: any) => {
    trackAnalyticsEvent('reg_flow_register_submit', {
      has_captcha: Boolean(captchaToken),
    });
    sessionStorage.setItem(SESSION_KEY_EMAIL, values.email);
    sessionStorage.setItem(SESSION_KEY_PASSWORD, values.password);
    register(values);
  };

  return (
    <Row>
      <Col xs={24}>
        <Typography.Title level={1} style={{ textAlign: 'center' }}>
          {t('Register')}
        </Typography.Title>
        <Form
          form={form}
          layout="vertical"
          style={{ fontSize: 18, marginTop: 20 }}
          onFinish={onFinish}
          validateTrigger="onChange"
        >
          <Form.Item
            name="email"
            style={{ fontSize: 18 }}
            rules={[{
              required: true,
              validator: async (_, value) => {
                if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                  return Promise.reject(new Error(`${t('RequireAValidEmail')}`));
                }
                if (!value) {
                  return Promise.reject(new Error(`${t('EmailIsRequired')}`));
                }
              },
            }]}
          >
            <Input placeholder={t('Email')} size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('PasswordIsRequired') },
              { min: 8, message: t('PasswordMustBeAtLeast8Characters') },
              { pattern: /^(?=.*[A-Z])(?=.*\d).{8,}$/, message: t('PasswordMustContainAtLeastOneUppercaseLetterAndOneNumber') },
            ]}
          >
            <Input.Password
              placeholder={t('Password')}
              size="large"
              type="password"
              style={{ padding: '0px 10px 0px 0px' }}
            />
          </Form.Item>
          <Form.Item required>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Switch
                checked={acceptTerms}
                onChange={(checked) => setAcceptTerms(checked)}
              />
              <Typography.Text style={{ fontSize: 15 }}>
                {t('AgreeToTerms')} <a href="https://www.farmersplan.com/villkor" target="_blank" rel="noreferrer">{t('termsAndConditions')}</a>
              </Typography.Text>
            </div>
          </Form.Item>
          <Form.Item>
            <Turnstile style={{ justifyContent: 'center', display: 'flex', marginBottom: 5 }} sitekey={cloudflareSiteKey} onVerify={(token: any) => setCaptchaToken(token)} theme="light" />
            {showTermsAlert && !acceptTerms && (
              <Alert
                message={t('MustAcceptTermsAbove')}
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
            <Button
              type="primary"
              size="large"
              style={{
                width: '100%',
                height: 52,
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                backgroundColor: !acceptTerms ? '#ccc' : '#ff6b3d',
                borderColor: !acceptTerms ? '#ccc' : '#ff6b3d',
                boxShadow: acceptTerms ? '0 14px 30px rgba(255, 107, 61, 0.22)' : undefined,
              }}
              onClick={async (e) => {
                e.preventDefault();
                if (!acceptTerms) {
                  trackAnalyticsEvent('reg_flow_register_blocked_terms');
                  setShowTermsAlert(true);
                  return;
                }
                form.submit();
              }}
            >
              {t('Continue')}
            </Button>
          </Form.Item>
        </Form>
      </Col>
      <Col xs={24} style={{ textAlign: 'center', marginTop: -10 }}>
        <Typography.Text>{t('AlreadyHaveAccount')} <a href="/">{t('SignIn')}</a></Typography.Text>
      </Col>
    </Row>
  );
}
